import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { appealHref } from '../appealLink';
import type { LogEntry as LogEntryT, TranscriptLine } from '../types';
import { mmss } from '../utils';
import { PMS } from '@/features/shared/pms';

function Tick({ icon = '✓' }: { icon?: string }) {
  return <span className="ew-tickicon" aria-hidden="true">{icon}</span>;
}

function timeToSec(t: string): number {
  const parts = t.split(':').map(Number);
  return (parts[0] ?? 0) * 60 + (parts[1] ?? 0);
}

function CallPlayer({ entry }: { entry: LogEntryT }) {
  const total = entry.durationSec ?? 0;
  const [pos, setPos] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const raf = useRef<number | null>(null);
  const last = useRef(0);

  useEffect(() => {
    if (!playing) return;
    last.current = performance.now();
    const tick = (now: number): void => {
      const dt = (now - last.current) / 1000;
      last.current = now;
      setPos((p) => {
        const next = p + dt * speed;
        if (next >= total) { setPlaying(false); return total; }
        return next;
      });
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [playing, speed, total]);

  const lines = entry.transcript ?? [];
  const current = (() => {
    let idx = -1;
    lines.forEach((l, i) => { if (timeToSec(l.time) <= pos) idx = i; });
    return idx;
  })();

  const seekTo = (sec: number): void => { setPos(Math.max(0, Math.min(total, sec))); };

  const copy = (): void => {
    const text = lines.map((l) => `${l.time}  ${l.speaker ? `${l.speaker}: ` : ''}${l.text}`).join('\n');
    try { void navigator.clipboard.writeText(text); } catch { /* mock */ }
  };

  return (
    <>
      <div className="ew-player">
        <div className="ew-player-row">
          <button type="button" className="ew-play" aria-label={playing ? 'Pause recording' : 'Play recording'} onClick={() => { setPlaying((p) => !p); }}>{playing ? '❚❚' : '▶'}</button>
          <div
            className="ew-seek"
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={Math.round(pos)}
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'ArrowRight') seekTo(pos + 5); if (e.key === 'ArrowLeft') seekTo(pos - 5); }}
            onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); seekTo(((e.clientX - r.left) / r.width) * total); }}
          >
            <span style={{ width: `${String(total ? (pos / total) * 100 : 0)}%` }} />
          </div>
          <span className="ew-meta num" style={{ whiteSpace: 'nowrap' }}>{mmss(Math.round(pos))} / {mmss(total)}</span>
          <button type="button" className="ew-btn" style={{ minHeight: 30, padding: '0 10px' }} onClick={() => { setSpeed((s) => (s === 1 ? 1.5 : s === 1.5 ? 2 : 1)); }}>{speed}×</button>
        </div>
      </div>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span className="ew-eyebrow">Transcript</span>
          <button type="button" className="ew-src" onClick={copy}>Copy</button>
        </div>
        <div className="ew-transcript">
          {lines.map((l: TranscriptLine, i) => (
            <div key={`${l.time}-${String(i)}`} className={`ew-tline${l.system ? ' sys' : ''}${i === current ? ' on' : ''}`}>
              <button type="button" className="tt" onClick={() => { seekTo(timeToSec(l.time)); }}>{l.time}</button>
              <div>{l.speaker ? <span className="sp">{l.speaker}: </span> : null}{l.text}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export function LogEntryPanel({ entry, onClose }: { entry: LogEntryT; onClose: () => void }) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => { headingRef.current?.focus(); }, [entry.id]);
  return (
    <aside className="ew-panel" aria-label="Log entry">
      <div className="ew-panel-inner">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
          <span className="ew-eyebrow">{entry.kindLabel}</span>
          <button type="button" className="ew-btn" style={{ minHeight: 30, padding: '0 10px' }} onClick={onClose}>Close</button>
        </div>
        <div>
          <h2 className="ew-h2" tabIndex={-1} ref={headingRef} style={{ margin: 0 }}>{entry.title ?? entry.what}</h2>
          <p className="ew-meta" style={{ marginTop: 4 }}>{entry.headerMeta}</p>
        </div>

        {entry.why ? (
          <div><span className="ew-eyebrow">Why</span><p style={{ margin: '4px 0 0', color: 'var(--fg2)' }}>{entry.why}{entry.whyLink && appealHref(entry.whyLink.href) ? <> <Link to={appealHref(entry.whyLink.href) ?? ''} className="ew-src">{entry.whyLink.label}</Link></> : null}</p></div>
        ) : null}

        {entry.kind === 'call' && entry.ticks ? (
          <div><span className="ew-eyebrow">What came of it</span>{entry.ticks.map((t, i) => <div key={`ct-${String(i)}`} className="ew-tick"><Tick />{t.text}</div>)}</div>
        ) : null}

        {entry.kind === 'call' ? <CallPlayer entry={entry} /> : null}

        {entry.steps ? (
          <div>
            <span className="ew-eyebrow">What ELVA did</span>
            {entry.steps.map((s, i) => <div key={`s-${String(i)}`} className="ew-logr"><span className="t">{s.time}</span><span>{s.text}</span></div>)}
          </div>
        ) : null}

        {entry.claimRows ? (
          <div>
            <span className="ew-eyebrow">The claims it sent</span>
            <table className="ew-tbl" style={{ marginTop: 6 }}>
              <thead><tr><th>Patient</th><th>Payer</th><th className="r">Amount</th><th>Clearinghouse response</th></tr></thead>
              <tbody>
                {entry.claimRows.map((c) => (
                  <tr key={`${c.patient}-${c.amount}`}><td>{c.patient}</td><td>{c.payer}</td><td className="r num">{c.amount}</td><td>{c.response}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {entry.changes ? (
          <div>
            <span className="ew-eyebrow">What changed in {PMS}</span>
            <table className="ew-tbl" style={{ marginTop: 6 }}><thead><tr><th>Record</th><th>Before</th><th>After</th></tr></thead>
              <tbody>{entry.changes.map((c) => <tr key={c.record}><td>{c.record}</td><td className="num">{c.before}</td><td className="num">{c.after}</td></tr>)}</tbody></table>
            {entry.changesNote ? <p className="ew-meta" style={{ marginTop: 6 }}>{entry.changesNote}</p> : null}
          </div>
        ) : null}

        {entry.failText ? (
          <div className="ew-card" style={{ background: 'var(--wsoft)', border: '1px solid #e9c98a', color: '#5c3c00' }}>
            <strong>{entry.failText}</strong>
            {entry.ticks?.map((t, i) => <div key={`ft-${String(i)}`} className="ew-tick" style={{ marginTop: 6 }}><Tick />{t.text}{t.link ? <> <Link to={t.link.href} className="ew-src">{t.link.label}</Link></> : null}</div>)}
            {entry.fixButton ? <div style={{ marginTop: 10 }}><Link to={entry.fixButton.href} className="ew-btn">{entry.fixButton.label}</Link></div> : null}
          </div>
        ) : null}

        {entry.personChanges ? (
          <div>
            <span className="ew-eyebrow">What changed</span>
            <table className="ew-tbl" style={{ marginTop: 6 }}><thead><tr><th>Setting</th><th>Before</th><th>After</th></tr></thead>
              <tbody>{entry.personChanges.map((c) => <tr key={c.record}><td>{c.record}</td><td>{c.before}</td><td>{c.after}</td></tr>)}</tbody></table>
            <p className="ew-meta" style={{ marginTop: 6 }}>{entry.personReason}</p>
          </div>
        ) : null}

        {entry.kind !== 'call' && entry.ticks && !entry.failText ? (
          <div>{entry.ticks.map((t, i) => <div key={`tk-${String(i)}`} className="ew-tick"><Tick />{t.text}{t.link ? <> <Link to={t.link.href} className="ew-src">{t.link.label}</Link></> : null}</div>)}</div>
        ) : null}

        {entry.fileLink ? <p><button type="button" className="ew-src">{entry.fileLink}</button></p> : null}
        {entry.sources ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px' }}>{entry.sources.map((s, i) => <button key={`src-${String(i)}`} type="button" className="ew-src">{s}</button>)}</div> : null}
        {entry.openLink ? <p><Link to={entry.openLink.href} className="ew-src">{entry.openLink.label}</Link></p> : null}
      </div>
    </aside>
  );
}
